import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { EventDispatcherService } from '../event-dispatcher/event-dispatcher.service';
import { takeUntil, Subject } from 'rxjs';

export interface Instruction {
  text: string;
}

export interface HelpContent {
  stepId: string;
  title: string;
  instructions: Instruction[];
  tips?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class WorkflowHelpPanelService {
  private isOpenSubject = new BehaviorSubject<boolean>(false);
  private currentContentSubject = new BehaviorSubject<HelpContent | null>(null);
  private destroy$ = new Subject<void>();
  private helpContentMap: { [key: string]: HelpContent } = {};

  public isOpen$ = this.isOpenSubject.asObservable();
  public currentContent$ = this.currentContentSubject.asObservable();

  constructor(private eventDispatcher: EventDispatcherService) {
    // 'logout' is dispatched on a manual logout and when the API answers 401 (expired session)
    this.eventDispatcher.subscribeToEvent('logout', () => this.closeHelp());
  }

  setHelpContentMap(helpContentMap: { [key: string]: HelpContent }): void {
    this.helpContentMap = { ...helpContentMap };
  }

  initializeHelpContent(workflowData: { [key: string]: any }): void {
    const helpContentMap: { [key: string]: HelpContent } = {};

    Object.keys(workflowData).forEach(stepId => {
      const workflowItem = workflowData[stepId];
      helpContentMap[stepId] = {
        stepId,
        title: workflowItem.title || 'Workflow Step',
        instructions: [
          { text: `Complete the ${workflowItem.title} workflow step` }
        ],
        tips: [
          'Follow compliance guidelines',
          'Document all actions taken',
          'Review periodically as required'
        ]
      };
    });

    this.setHelpContentMap(helpContentMap);
  }

  openHelp(stepId: string): void {
    const content = this.helpContentMap[stepId];
    
    if (content) {
      this.currentContentSubject.next(content);
      setTimeout(() => {
        this.isOpenSubject.next(true);
      }, 0);
    }
  }

  closeHelp(): void {
    this.isOpenSubject.next(false);
    setTimeout(() => {
      this.currentContentSubject.next(null);
    }, 300);
  }

  addHelpContent(stepId: string, content: HelpContent): void {
    this.helpContentMap[stepId] = content;
  }

  getHelpContent(stepId: string): HelpContent | undefined {
    return this.helpContentMap[stepId];
  }

  getHelpContentMap(): { [key: string]: HelpContent } {
    return this.helpContentMap;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}